/**
 * The template validator, T-7.2. Pure: it reads a spec and a catalogue,
 * returns a list of problems, and never throws, so the build script and a
 * future import endpoint can share it.
 *
 * The field and binding rules are not restated here. `validateFieldDefinition`
 * and `validateBinding` already hold them, so this file maps each spec field
 * onto their input shape and adds only what a whole spec can check and a lone
 * field cannot: slot collisions, real slot names, cross-record references,
 * ord collisions, the directory money rule, and that every key a spec carries
 * resolves in the catalogue. Business words never enter the schema; this is
 * where that rule becomes mechanical.
 */
import { ENTRY_BATCH_MAX, LINES_MAX_DEPTH, LINES_MAX_FIELDS, isCaptureTone, isRecordIconId } from '../api';
import { approvalChainProblems, readSpecApprovalChain } from './approval-chain';
import { chargePlanProblems, readSpecChargePlan } from './charge-plan';
import { accountsListProblems, readSpecStockItems, ruleProblems, stockItemsProblems, type RuleRecord } from './posting';
import { ENTRY_SLOT_COLUMNS } from '../entry-slots';
import {
  checkFormula,
  choiceOf,
  defaultProblems,
  filterProblems,
  formulaOf,
  isComputedType,
  readFieldSpec,
  readFormulaSpec,
  readRollupSpec,
  readsOnlyWorkedOut,
  rollupProblems,
  showWhenProblems,
  editableWhenProblems,
  mustBeProblems,
  MUST_BE_MAX,
  RECORD_RULES_MAX,
  readMustBeRule,
  readMustBeRules,
  linesStartHolds,
  LINES_START_MAX,
  slotFamilyFor,
  type ChoiceFieldInfo,
  type ChoiceLookup,
  type FormulaFieldInfo,
  type FormulaLookup,
  type FormulaSpec,
  type RollupFieldInfo,
  type RollupResult,
} from '../formula';
import { fieldSeriesProblems, readSeriesSpec } from '../field-series';
import { openRowProblems, readOpenRow } from '../open-row';
import { readSpecTaskRule, specTaskRuleProblems, TASK_RULES_MAX } from '../task-rules';
import { deepTotalsThrough, fieldGraph, nodeOf, ringProblem, ringsOf, type GraphField, type GraphRecord } from '../field-graph';
import { validateBinding, validateFieldDefinition } from '../registry/validate';
import { seedOrder, seedWordsMatch, titleFieldKeyOf } from './seed-references';
import { VALUE_TYPES, isValueTypeId } from '../generated/value-types';
import { INSIGHT_CARD_IDS, INSIGHT_CARD_SHAPE, isInsightCardId } from '../insights';
import { judgeLayout } from '../layout';
import { LINES_PARENT_KEY, LINES_ROOT_KEY, linesRecordsOf, type LinesRecordSpec, type SpecRecordOrLines } from './lines';
import type {
  SpecField,
  SpecRecord,
  SetupSpec,
} from './spec';

/**
 * What a caller may relax, and nothing else.
 *
 *   allowCustomTier  a `custom` field is accepted. Off for the JSON pack, where
 *                    a template never ships a custom field (Section 3.2). On
 *                    for a spec composed at run time from a person's own words:
 *                    those fields are the person's from the moment they exist,
 *                    the same reason `blank.ts` gives, and the only honest tier
 *                    for them is `custom`.
 */
export type SpecValidateOptions = {
  readonly allowCustomTier?: boolean;
};

/**
 * The operators one field type may be asked about.
 *
 * The same rule the app's editor follows (`opsForField` in
 * `insight-draft.ts`), and this is the build-time half of it. A category is
 * asked what it is; a number how big it is; a tick yes or no; everything else
 * only whether it was answered. Equality on free text is absent on purpose: it
 * looks like a search and behaves like an exact match.
 */
const OPS_BY_TYPE: Readonly<Record<string, readonly string[]>> = {
  select: ['is', 'is_not', 'set', 'unset'],
  reference: ['set', 'unset'],
  money: ['gt', 'gte', 'lt', 'lte', 'set', 'unset'],
  number: ['gt', 'gte', 'lt', 'lte', 'set', 'unset'],
  quantity: ['gt', 'gte', 'lt', 'lte', 'set', 'unset'],
  boolean: ['is'],
};

const PRESENCE_OPS: readonly string[] = ['set', 'unset'];
const NUMERIC_OPS: readonly string[] = ['gt', 'gte', 'lt', 'lte'];

/**
 * Every condition on one tile, checked against the field it names.
 *
 * A device can only ever DROP a condition it cannot compile, and dropping one
 * silently would leave a card called "Open dues" counting the settled ones —
 * a bigger number under a name that promised a smaller. So these are caught
 * here, at build time, where a person can read the message.
 *
 * A condition must also live in a slot: a `multi_select` is a set of rows in
 * `data.entry_link` with no column to read, and the computed types are worked
 * out server-side.
 */
const tileConditionProblems = (
  where: unknown,
  record: Record<string, unknown>,
  path: string,
): SpecProblem[] => {
  if (where === undefined) return [];
  if (!Array.isArray(where)) {
    return [{ code: 'TILE_WHERE_NOT_LIST', message: 'A tile where must be a list of conditions.', path }];
  }

  const problems: SpecProblem[] = [];
  const fields = (Array.isArray(record.fields) ? record.fields : []).filter(isObject);

  for (const condition of where) {
    if (!isObject(condition)) {
      problems.push({ code: 'TILE_CONDITION_NOT_OBJECT', message: 'A tile condition must be an object.', path });
      continue;
    }
    const key = String(condition.fieldKey);
    const field = fields.find((held) => String(held.key) === key);
    if (field === undefined) {
      problems.push({
        code: 'TILE_CONDITION_FIELD_UNKNOWN',
        message: `Tile condition names field "${key}", which this record does not declare.`,
        path,
      });
      continue;
    }

    const type = String(field.type);
    const op = String(condition.op);
    // A worked-out field is asked as what it works out to, as the app's editor asks it (`opsForField`):
    // a Balance that is money is asked how big it is ("dues above 0"), never only whether it is there.
    const result = type === 'formula' ? formulaOf(readFieldSpec(field.spec as never))?.result : undefined;
    const allowed = (result === 'money' || result === 'number' ? OPS_BY_TYPE[result] : OPS_BY_TYPE[type]) ?? PRESENCE_OPS;
    if (!allowed.includes(op)) {
      problems.push({
        code: 'TILE_CONDITION_OP_INVALID',
        message: `Tile condition "${op}" is not one a ${type} field can be asked (${allowed.join(', ')}).`,
        path,
      });
      continue;
    }
    if (field.storage !== undefined && field.storage !== 'slot') {
      problems.push({
        code: 'TILE_CONDITION_FIELD_UNREADABLE',
        message: `Tile condition names "${key}", which is not stored in a slot, so no card could read it.`,
        path,
      });
    }

    if (PRESENCE_OPS.includes(op)) continue;

    if (NUMERIC_OPS.includes(op) || type === 'boolean') {
      if (typeof condition.num !== 'number' || !Number.isFinite(condition.num)) {
        problems.push({
          code: 'TILE_CONDITION_NUM_MISSING',
          message: `Tile condition "${op}" on "${key}" needs a num.`,
          path,
        });
      }
      if (type === 'boolean' && condition.num !== 0 && condition.num !== 1) {
        problems.push({
          code: 'TILE_CONDITION_NUM_INVALID',
          message: `Tile condition on the tick "${key}" takes num 1 for yes or 0 for no.`,
          path,
        });
      }
      continue;
    }

    // What is left is `is` or `is_not` on a select, so it names one of that
    // field's own choices. Apply rewrites the key to the id it mints.
    const choices = (Array.isArray(field.options) ? field.options : []).filter(isObject);
    const optionKey = condition.optionKey;
    if (typeof optionKey !== 'string' || !choices.some((held) => String(held.key) === optionKey)) {
      problems.push({
        code: 'TILE_CONDITION_OPTION_UNKNOWN',
        message: `Tile condition names choice "${String(optionKey)}", which "${key}" does not declare.`,
        path,
      });
    }
  }

  return problems;
};

/**
 * A summary tile pins an insight card on the record's entry list.
 *
 * Four of its keys were always there — `label_key`, `expr`, `scope`, `style`.
 * The rest let a template pin a chart rather than only a number, and every
 * one of them is checked here rather than on the device, because a device can
 * only ever drop a tile it cannot draw, silently.
 *
 * `card` must be a card some client actually has. `group_by` must name a field
 * of this record, because apply rewrites it to that field's id and a key
 * nothing owns would become a lookup failure at apply time, not a wrong card.
 * A breakdown must have something to group by, or it is not a breakdown.
 */
const summaryTileProblems = (
  tile: unknown,
  record: Record<string, unknown>,
  path: string,
): SpecProblem[] => {
  const problems: SpecProblem[] = [];
  if (!isObject(tile)) {
    return [{ code: 'SUMMARY_TILE_NOT_OBJECT', message: 'A summary tile must be an object.', path }];
  }

  const card = tile.card;
  if (card !== undefined && !isInsightCardId(card)) {
    problems.push({
      code: 'SUMMARY_CARD_UNKNOWN',
      message:
        `Summary tile card "${String(card)}" is not an insight card. ` +
        `The cards are ${INSIGHT_CARD_IDS.join(', ')}.`,
      path,
    });
    return problems;
  }

  const fieldKeys = new Set(
    (Array.isArray(record.fields) ? record.fields : [])
      .filter(isObject)
      .map((field) => String(field.key)),
  );

  const groupBy = tile.group_by;
  if (groupBy !== undefined) {
    if (typeof groupBy !== 'string' || !fieldKeys.has(groupBy)) {
      problems.push({
        code: 'SUMMARY_GROUP_BY_UNKNOWN',
        message: `Summary tile group_by "${String(groupBy)}" is not a field of this record.`,
        path,
      });
    }
  }

  for (const problem of tileConditionProblems(tile.where, record, path)) problems.push(problem);

  const shape = card === undefined ? 'scalar' : INSIGHT_CARD_SHAPE[card];

  // `argmax:<key> by ...` already names a group, so a breakdown may take it
  // from the expr instead of repeating the field.
  const namesGroupInExpr = /^argmax:[a-z][a-z0-9_]*\s+by\s+/.test(String(tile.expr ?? ''));
  if (shape === 'breakdown' && groupBy === undefined && !namesGroupInExpr) {
    problems.push({
      code: 'SUMMARY_GROUP_BY_MISSING',
      message: 'A breakdown tile needs group_by, or an argmax expr that names the group.',
      path,
    });
  }

  if (shape === 'scalar_pair' && tile.scope === 'all') {
    problems.push({
      code: 'SUMMARY_SCOPE_IMPOSSIBLE',
      message: 'A tile that compares against the previous period cannot be scoped to all time.',
      path,
    });
  }

  for (const [key, low] of [
    ['days', 1],
    ['limit', 1],
  ] as const) {
    const held = tile[key];
    if (held === undefined) continue;
    if (typeof held !== 'number' || !Number.isInteger(held) || held < low) {
      problems.push({
        code: 'SUMMARY_COUNT_INVALID',
        message: `Summary tile ${key} must be a whole number of ${low} or more.`,
        path,
      });
    }
  }

  return problems;
};

export type SpecProblem = {
  /** Stable, machine readable. Safe to branch on. */
  readonly code: string;
  /** Plain English. Names the record, the field, or the key at fault. */
  readonly message: string;
  /** Where in the spec, such as `daily-sales.records[sales].fields[amount]`. */
  readonly path: string;
};

export type Catalogue = Readonly<Record<string, string>>;

const SLOT_LETTER_FOR_HOME: Readonly<Record<string, string>> = {
  numeric_slot: 'n',
  text_slot: 't',
  time_slot: 'd',
  reference_slot: 'r',
  boolean_slot: 'b',
};

const KEY_PATTERN = /^[a-z][a-z0-9_]*$/;

// The validator promises to return problems and never throw, so a list read
// from JSON is walked only after each element proves to be an object.
const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/** A word is a word when it is a non-empty string. */
const spelled = (catalogue: Catalogue, key: string): boolean =>
  typeof catalogue[key] === 'string' && catalogue[key].length > 0;

/**
 * A spec's key resolves as a leaf, or as a plural STEM.
 *
 * `blank.ts` names its record `noun.record` and its entries `noun.entry`, and
 * those are stems now: the catalogue holds `noun.record_one` and
 * `noun.record_other` and nothing called `noun.record`, because i18next picks
 * the form from the count. A stem is exactly as resolvable as a leaf — the
 * device says it with a count — so a spec that names one is not a defect, and
 * a spec that names half a pair is.
 */
function resolveKey(
  catalogue: Catalogue,
  key: unknown,
  path: string,
  what: string,
): SpecProblem | null {
  if (typeof key !== 'string' || key.length === 0) {
    return { code: 'LABEL_KEY_MISSING', message: `${what} is missing.`, path };
  }
  const stem = spelled(catalogue, `${key}_one`) && spelled(catalogue, `${key}_other`);
  if (!spelled(catalogue, key) && !stem) {
    return {
      code: 'LABEL_KEY_UNKNOWN',
      message: `${what} "${key}" resolves to nothing in the i18n catalogue.`,
      path,
    };
  }
  return null;
}

/**
 * Onboarding answers are gone, so no answer tag is known: a spec that still
 * names one is refused rather than left to mean "never ships", which is the
 * worst kind of quiet. Apply ships every piece and takes every base word.
 */
const checkAnswerTags = (
  tags: unknown,
  path: string,
  what: string,
  push: (p: SpecProblem | null) => void,
): void => {
  if (tags === undefined) return;
  if (!Array.isArray(tags) || tags.length === 0) {
    push({ code: 'ANSWER_TAGS_INVALID', message: `${what} must be a non-empty list of answer tags.`, path });
    return;
  }
  for (const tag of tags) {
    push({
      code: 'ANSWER_TAG_UNKNOWN',
      message: `${what} names "${String(tag)}", which no onboarding question can answer.`,
      path,
    });
  }
};

/** A `labelKeyBy` or `nameKeyBy` map: real tag on the left, real word on the right. */
const checkKeyBy = (
  by: unknown,
  catalogue: Catalogue,
  path: string,
  what: string,
  push: (p: SpecProblem | null) => void,
): void => {
  if (by === undefined) return;
  if (!isObject(by) || Object.keys(by).length === 0) {
    push({ code: 'KEY_BY_INVALID', message: `${what} must be a non-empty object.`, path });
    return;
  }
  for (const [tag, key] of Object.entries(by)) {
    push({
      code: 'ANSWER_TAG_UNKNOWN',
      message: `${what} names "${tag}", which no onboarding question can answer.`,
      path,
    });
    push(resolveKey(catalogue, key, path, `${what} word for "${tag}"`));
  }
};

/**
 * Every field key the views of one record name, through the closed vocabulary
 * of app.view.spec. A field any view names can never be dropped by an answer:
 * the stored view would point at a field id that was never minted.
 */
const viewFieldKeys = (record: SpecRecordOrLines): Set<string> => {
  const keys = new Set<string>();
  const walkFilter = (node: unknown): void => {
    if (!isObject(node)) return;
    if (typeof node.fieldKey === 'string') keys.add(node.fieldKey);
    for (const child of Array.isArray(node.children) ? node.children : []) walkFilter(child);
  };

  for (const view of Array.isArray(record.views) ? record.views : []) {
    if (!isObject(view) || !isObject(view.spec)) continue;
    const spec = view.spec;
    if (typeof spec.group_by === 'string') keys.add(spec.group_by);
    for (const rule of Array.isArray(spec.sort) ? spec.sort : []) {
      if (isObject(rule) && typeof rule.fieldKey === 'string') keys.add(rule.fieldKey);
    }
    for (const listKey of ['field_order', 'hidden_fields'] as const) {
      for (const key of Array.isArray(spec[listKey]) ? (spec[listKey] as unknown[]) : []) {
        if (typeof key === 'string') keys.add(key);
      }
    }
    if (isObject(spec.component_override)) {
      for (const key of Object.keys(spec.component_override)) keys.add(key);
    }
    walkFilter(spec.filter);
    for (const tile of Array.isArray(spec.summaries) ? spec.summaries : []) {
      if (!isObject(tile)) continue;
      // An expr is `op:fieldKey`, optionally twice around ` by `.
      for (const match of String(tile.expr ?? '').matchAll(/[a-z_]+:([a-z][a-z0-9_]*)/g)) {
        keys.add(match[1]!);
      }
      if (typeof tile.group_by === 'string') keys.add(tile.group_by);
      for (const condition of Array.isArray(tile.where) ? tile.where : []) {
        if (isObject(condition) && typeof condition.fieldKey === 'string') keys.add(condition.fieldKey);
      }
    }
  }
  return keys;
};

/**
 * Every record of a spec and every lines record its `lines` fields hold,
 * written out as records (spec/lines.ts). Worked out once per spec: the
 * lookups below ask it for every field.
 */
const withLines = new WeakMap<object, SpecRecordOrLines[]>();
const recordsOf = (spec: SetupSpec): SpecRecordOrLines[] => {
  const held = withLines.get(spec);
  if (held !== undefined) return held;
  const top = (Array.isArray(spec.records) ? spec.records : []).filter(isObject) as SpecRecordOrLines[];
  const all = [...top, ...linesRecordsOf(top, `${String(spec.id ?? '(no id)')}.`)];
  withLines.set(spec, all);
  return all;
};

/** The two fields of a lines record that point up its tree. */
/** The title types a picked record may carry: words, so the pick reads as a name. */
export const TEXT_TITLE_TYPES: ReadonlySet<string> = new Set(['text', 'long_text', 'phone']);

/** The type of the field a record's title binding names, or null when it names none. */
const pickTitleOf = (record: Record<string, unknown>): string | null => {
  const bindings = Array.isArray(record.bindings) ? record.bindings : [];
  const title = bindings.find((one) => isObject(one) && one.purpose === 'title') as { fieldKey?: unknown } | undefined;
  if (title === undefined || typeof title.fieldKey !== 'string') return null;
  const fields = Array.isArray(record.fields) ? record.fields : [];
  const field = fields.find((one) => isObject(one) && one.key === title.fieldKey) as { type?: unknown } | undefined;
  return typeof field?.type === 'string' ? field.type : null;
};

const isTreeField = (record: SpecRecordOrLines, key: string): boolean =>
  record.kind === 'lines' && (key === LINES_PARENT_KEY || key === LINES_ROOT_KEY);

/** How deep a record sits: 0 for a record of the spec, 1 or 2 for a lines record. */
const depthOf = (record: SpecRecordOrLines): number => (record.kind === 'lines' ? (record as LinesRecordSpec).depth : 0);

/**
 * What the formula checker asks about, answered from the spec's own keys.
 *
 * The same three questions `formulaLookup` in the server's structure planner
 * answers from live rows, asked here of a spec that has no rows yet: a node
 * names a field of this record, a `hop` goes through a reference field of this
 * record into the directory it targets. Keys stand in for ids on both sides,
 * which is exactly what apply will rewrite.
 */
const specFormulaLookup = (
  spec: SetupSpec,
  record: SpecRecordOrLines,
  /**
   * A formula (9.3) and a fill-once default (F11 of
   * docs/plans/field-relations.md) may read a worked-out field, so their
   * lookup says what each formula works out to. A plain default, Show when
   * and Editable when still refuse one, by its type.
   */
  options: { readonly formulaResults?: boolean } = {},
): FormulaLookup => {
  // A rollup is read as a stored value of what it adds up (Phase 4.3), which
  // a spec leaves for apply to write: worked out here from `of` on `over`.
  const info = (field: SpecField): FormulaFieldInfo => {
    const result =
      field.type === 'rollup'
        ? specRollupResult(spec, field)
        : field.type === 'formula' && options.formulaResults === true
          ? formulaOf(readFieldSpec(field.spec))?.result
          : undefined;
    return { id: field.key, label: field.labelKey, type: field.type, ...(result === undefined ? {} : { result }) };
  };
  /**
   * A field on each line, saying what a worked-out one works out to: a total
   * over lines may read a line's own formula (the Amount of each room), which
   * Decision 8 works out first.
   */
  const lineInfo = (field: SpecField): FormulaFieldInfo => {
    const result = field.type === 'formula' ? formulaOf(readFieldSpec(field.spec))?.result : undefined;
    return { ...info(field), ...(result === undefined ? {} : { result }) };
  };
  const own = new Map(
    (Array.isArray(record.fields) ? record.fields : []).filter(isObject).map((field) => [field.key, field]),
  );
  const records = recordsOf(spec);

  /** One field of the record `viaKey` points at, or null. Where a hop lands. */
  const there = (viaKey: string, key: string): SpecField | null => {
    const via = own.get(viaKey);
    if (via === undefined || via.type !== 'reference' || via.targetRecordKey === undefined) return null;
    const held = records.find((one) => one.key === via.targetRecordKey);
    const field = (Array.isArray(held?.fields) ? held.fields : [])
      .filter(isObject)
      .find((one) => one.key === key);
    return field ?? null;
  };

  /**
   * One field of the rows `linesKey` holds: what an `agg` reads. A multi_select
   * holds rows of the list it points at; a `lines` field holds the lines of its
   * own lines record, line formulas included.
   */
  const onLines = (linesKey: string, key: string): SpecField | null => {
    const lines = own.get(linesKey);
    if (lines === undefined) return null;
    const targetKey =
      lines.type === 'multi_select'
        ? lines.targetRecordKey
        : lines.type === 'lines' && isObject(lines.lines)
          ? lines.lines.key
          : undefined;
    if (targetKey === undefined) return null;
    const held = records.find((one) => one.key === targetKey);
    return (Array.isArray(held?.fields) ? held.fields : []).filter(isObject).find((one) => one.key === key) ?? null;
  };

  return {
    own: (key) => {
      const field = own.get(key);
      return field === undefined ? null : info(field);
    },
    isReference: (key) => own.get(key)?.type === 'reference',
    hop: (viaKey, key) => {
      const field = there(viaKey, key);
      return field === null ? null : info(field);
    },
    lineField: (linesKey, key) => {
      const field = onLines(linesKey, key);
      if (field === null) return null;
      // A multi_select's list holds no formula a total may read; a line does.
      return own.get(linesKey)?.type === 'lines' ? lineInfo(field) : info(field);
    },
    // A line reads only what was TYPED above it: a formula above can total the
    // lines below, so a line reading one would make a ring.
    ...(record.kind === 'lines'
      ? {
          isParentComputed: (viaKey: string, key: string): boolean => {
            if (viaKey !== LINES_PARENT_KEY && viaKey !== LINES_ROOT_KEY) return false;
            const field = there(viaKey, key);
            return field !== null && isComputedType(field.type);
          },
        }
      : {}),
    // Both of these serve `defaultProblems` and nothing else. A spec has no
    // ids, so a choice is named by its option KEY and a list by its record KEY,
    // which is exactly what apply rewrites and therefore exactly what "the same
    // list" has to mean here.
    optionsOf: (key) => own.get(key)?.options?.map((option) => option.key) ?? null,
    // What a `date_add` reads its unit from (F4): the choice keys of a select,
    // on this record or across a hop, so a select of no unit is refused here.
    optionKeysOf: (key, viaKey) => {
      const field = viaKey === undefined ? (own.get(key) ?? null) : there(viaKey, key);
      if (field === null || !Array.isArray(field.options)) return null;
      return field.options.filter(isObject).flatMap((option) => (typeof option.key === 'string' ? [option.key] : []));
    },
    targetOf: (key, viaKey) => {
      const field = viaKey === undefined ? (own.get(key) ?? null) : there(viaKey, key);
      return field?.targetRecordKey ?? null;
    },
  };
};

/** What a rollup of a spec adds up to: a count is a number, a sum, a min or a max the type of its `of`. */
const specRollupResult = (spec: SetupSpec, field: SpecField): RollupResult | undefined => {
  const read = readRollupSpec(isObject(field.spec) ? (field.spec as { rollup?: unknown }).rollup : undefined);
  if (read === null) return undefined;
  if (read.agg === 'count') return 'number';
  const of = fieldsOfRecord(spec, read.over).find((one) => one.key === read.of);
  const type = of?.type === 'formula' ? formulaOf(readFieldSpec(of.spec))?.result : of?.type;
  if (type === 'date') return read.agg === 'min' || read.agg === 'max' ? type : undefined;
  return type === 'number' || type === 'money' || type === 'quantity' ? type : undefined;
};

/** The slot family a spec's rollup answers into: `d` for a date, else `n`. */
const rollupSlotLetter = (field: SpecField, spec: SetupSpec): 'n' | 'd' =>
  specRollupResult(spec, field) === 'date' ? 'd' : 'n';

/** The rings and the totals of totals in a whole spec, from the field graph. */
const specGraphProblems = (spec: SetupSpec): SpecProblem[] => {
  const all = recordsOf(spec);
  const records: GraphRecord[] = all.map((record) => ({
    id: record.key,
    fields: (Array.isArray(record.fields) ? record.fields : []).filter(isObject).map(
      (field): GraphField => ({
        id: field.key,
        label: field.key,
        type: field.type,
        targetRecordId: field.targetRecordKey ?? null,
        spec: field.spec,
        startsWith: field.startsWith === undefined ? null : readFormulaSpec(field.startsWith),
        startsOnce: field.startsOnce === true,
        showWhen: field.showWhen === undefined ? null : readFormulaSpec(field.showWhen),
        editableWhen: field.editableWhen === undefined ? null : readFormulaSpec(field.editableWhen),
        // A setup draft's Must be rules (Phase 6) are rule nodes too: each reads, none writes.
        mustBe: readMustBeRules(field.mustBe).map((rule) => rule.formula),
        linesRecordId: field.type === 'lines' && isObject(field.lines) ? (field.lines as { key?: string }).key ?? null : null,
      }),
    ),
    // The rules on the whole entry (`prefs.rules`); a lines record holds none.
    rules: readMustBeRules((record as SpecRecord).rules).map((rule) => rule.formula),
  }));
  const graph = fieldGraph(records);
  const pathOf = (node: string | undefined): string => {
    const [recordKey = '', fieldKey = ''] = (node ?? '').split('|');
    const record = all.find((one) => one.key === recordKey);
    const base = record !== undefined && 'path' in record ? String((record as LinesRecordSpec).path) : `${String(spec.id ?? '(no id)')}.records[${recordKey}]`;
    return `${base}.fields[${fieldKey}]`;
  };
  const out: SpecProblem[] = [];
  for (const ring of ringsOf(graph)) {
    // A field that reads itself is named where its recipe is checked.
    if (ring.length === 1) continue;
    const found = ringProblem(graph, ring);
    out.push({ code: found.code, message: found.message, path: pathOf(ring[0]) });
  }
  const everything = records.flatMap((record) => record.fields.map((field) => nodeOf(record.id, field.id)));
  for (const found of deepTotalsThrough(graph, records, everything)) {
    out.push({ code: found.code, message: found.message, path: pathOf(everything.find((node) => node.endsWith(`|${found.fieldId ?? ''}`))) });
  }
  return out;
};

/** The fields of the record a spec keys as `recordKey`, or none. */
const fieldsOfRecord = (spec: SetupSpec, recordKey: string | undefined): SpecField[] => {
  if (recordKey === undefined) return [];
  const held = recordsOf(spec).find((one) => one.key === recordKey);
  return (Array.isArray(held?.fields) ? held.fields : []).filter(isObject);
};

/**
 * The filter and limit of a `reference` or `multi_select`, held to
 * `filterProblems`, the rule the structure planner holds a person's own edit
 * to. A spec has no ids, so keys stand in for them on both sides: a field of
 * this record by its key, a field of the target list by its key, and a list
 * by its record KEY, which is exactly what apply rewrites.
 */
const checkChoiceSpec = (
  field: SpecField,
  spec: SetupSpec,
  record: SpecRecordOrLines,
  path: string,
  push: (p: SpecProblem | null) => void,
): void => {
  const read = choiceOf(readFieldSpec(field.spec));
  if (read === null) {
    push({
      code: 'FILTER_SHAPE',
      message: `The spec of "${field.key}" is not a filter or a limit this version can read.`,
      path,
    });
    return;
  }
  const info = (one: SpecField): ChoiceFieldInfo => ({
    id: one.key,
    type: one.type,
    targetRecordId: one.targetRecordKey ?? null,
    filter: choiceOf(readFieldSpec(one.spec))?.filter ?? null,
  });
  const own = fieldsOfRecord(spec, record.key);
  const lookup: ChoiceLookup = {
    own: (key) => {
      const found = own.find((one) => one.key === key);
      return found === undefined ? null : info(found);
    },
    onTarget: (targetKey, key) => {
      const found = fieldsOfRecord(spec, targetKey).find((one) => one.key === key);
      return found === undefined ? null : info(found);
    },
  };
  for (const problem of filterProblems(info(field), read, lookup)) {
    push({ code: problem.code, message: problem.message, path });
  }
};

/**
 * A `rollup` field: the total of another record's entries that pick this row
 * (Phase 4 of docs/plans/playbooks-and-room-bill.md), held to `rollupProblems`,
 * the rule the structure planner holds a person's own add to. Keys stand in
 * for ids, as everywhere in a spec: `over` a record key, `via`, `of` and each
 * `where.field` a field key of that record, `where.equals` an option key.
 */
const checkRollupSpec = (
  field: SpecField,
  spec: SetupSpec,
  record: SpecRecordOrLines,
  path: string,
  push: (p: SpecProblem | null) => void,
): void => {
  if (field.required === true) {
    push({ code: 'FORMULA_NEVER_REQUIRED', message: 'A total of other entries cannot be required. Nobody fills it.', path });
  }
  // An entry of a record or a row of a list adds up the entries that pick it
  // (a member's Current expiry, on a list of people); the lines of an entry
  // are never picked, so they hold none.
  if (record.kind !== 'record' && record.kind !== 'directory') {
    push({ code: 'ROLLUP_SHAPE', message: 'The lines of an entry cannot hold a total of other entries.', path });
    return;
  }
  const raw = isObject(field.spec) ? (field.spec as { rollup?: unknown }).rollup : undefined;
  const read = raw === undefined ? null : readRollupSpec(raw);
  if (read === null) {
    push({
      code: 'ROLLUP_SHAPE',
      message: `"${field.key}" needs spec.rollup: { agg, over, via, of?, where? }, a total this version can read.`,
      path,
    });
    return;
  }
  const records = (Array.isArray(spec.records) ? spec.records : []).filter(isObject);
  const info = (one: SpecField): RollupFieldInfo => ({
    id: one.key,
    label: one.key,
    type: one.type,
    targetRecordId: one.targetRecordKey ?? null,
    optionIds: one.options?.map((option) => option.key) ?? null,
    formula: one.type === 'formula' ? formulaOf(readFieldSpec(one.spec)) : null,
  });
  const found = rollupProblems(read, record.key, {
    recordKnown: (key) => records.some((one) => one.key === key && one.kind === 'record'),
    field: (recordKey, key) => {
      const held = fieldsOfRecord(spec, recordKey).find((one) => one.key === key);
      return held === undefined ? null : info(held);
    },
  });
  for (const problem of found.problems) push({ code: problem.code, message: problem.message, path });
  // A total of money is allowed on a list (W11 of docs/plans/workbook-map.md,
  // the middle way): the money is worked out from records, never typed, and
  // a card reads it only over the list's own rows (LIST_MONEY_NOT_TOTALLED).
  // The pick is read the other way to add up the bill, so it lives in a
  // reference slot with an index (r1 or r2), as the planner puts it.
  const via = fieldsOfRecord(spec, read.over).find((one) => one.key === read.via);
  if (via !== undefined && via.storage === 'slot' && via.slot !== undefined && via.slot !== 'r1' && via.slot !== 'r2') {
    push({
      code: 'ROLLUP_VIA',
      message: `"${read.via}" is added up from this side, so it must live in r1 or r2, the reference slots with an index.`,
      path,
    });
  }
};

/**
 * A formula field's recipe, or null when it has none it can use.
 *
 * Three refusals, in the order the planner makes them: a formula with no
 * recipe, a recipe this version cannot read, a recipe that reads what is not
 * there or adds what cannot be added. A non-formula field carrying a recipe is
 * refused too, so `spec` can never quietly ride on a text field.
 */
/**
 * A `series` field: a number each entry is given in turn (W17 to W20 of
 * docs/plans/workbook-map.md). Its shape is held to `fieldSeriesProblems`, and
 * "Counted separately for each" (`per`) names a choice or a pick of the same
 * record, by key. A line is saved with its entry, never on its own, so the
 * lines of an entry hold no series.
 */
const checkSeriesSpec = (
  field: SpecField,
  record: SpecRecordOrLines,
  path: string,
  push: (p: SpecProblem | null) => void,
): void => {
  if (record.kind === 'lines') {
    push({ code: 'SERIES_SHAPE', message: 'The lines of an entry hold no number series. Number the entry instead.', path });
    return;
  }
  const raw = isObject(field.spec) ? (field.spec as { series?: unknown }).series : {};
  const read = readSeriesSpec(raw ?? {});
  if (read === null) {
    push({
      code: 'SERIES_SHAPE',
      message: `"${field.key}" needs spec.series: { prefix?, yearStyle?, digits?, startNumber?, reset?, fyStartMonth?, per?, once? }.`,
      path,
    });
    return;
  }
  const problems = fieldSeriesProblems(read);
  if (problems.length > 0) {
    push({
      code: 'SERIES_SHAPE',
      message: `"${field.key}" has a series shape that cannot hold (${problems.join(', ')}): a prefix of capital letters or none, 1 to 6 digits, a start from 1, and at most 16 characters.`,
      path,
    });
  }
  if (read.per !== null) {
    const per = (Array.isArray(record.fields) ? record.fields : []).filter(isObject).find((one) => one.key === read.per);
    if (per === undefined || (per.type !== 'select' && per.type !== 'reference')) {
      push({
        code: 'SERIES_PER',
        message: `"${field.key}" is counted separately for each "${read.per}", which must be a choice or a pick on the same record.`,
        path,
      });
    }
  }
};

const checkFormulaSpec = (
  field: SpecField,
  spec: SetupSpec,
  record: SpecRecordOrLines,
  path: string,
  push: (p: SpecProblem | null) => void,
): FormulaSpec | null => {
  if (field.type !== 'formula') {
    if (field.type === 'rollup') {
      checkRollupSpec(field, spec, record, path, push);
      return null;
    }
    if (field.type === 'series') {
      checkSeriesSpec(field, record, path, push);
      return null;
    }
    if (field.spec === undefined) return null;
    const held = field.spec as Record<string, unknown> | null;
    if (field.type === 'reference' || field.type === 'multi_select') {
      checkChoiceSpec(field, spec, record, path, push);
    } else if (typeof held === 'object' && held !== null && !('formula' in held) && ('filter' in held || 'maxChosen' in held)) {
      // A filter or a limit on a field that points at no list is refused as a
      // filter, so a mend takes the filter off and keeps the field.
      push({ code: 'FILTER_NOT_CHOICE', message: `A "${field.type}" field points at no list, so it cannot be filtered or limited.`, path });
    } else {
      push({ code: 'SPEC_UNEXPECTED', message: `A "${field.type}" field carries no formula.`, path });
    }
    return null;
  }
  if (field.spec === undefined) {
    push({ code: 'FORMULA_SPEC_MISSING', message: 'A formula field must carry its recipe in spec.', path });
    return null;
  }
  const read = readFormulaSpec((field.spec as { formula?: unknown }).formula);
  if (read === null) {
    push({ code: 'FORMULA_SHAPE', message: 'The formula is not a tree this version can read.', path });
    return null;
  }
  // field_computed_never_required in 0001, and FORMULA_NEVER_REQUIRED in the
  // planner: nobody fills a worked-out field, so it cannot be one that must be.
  if (field.required === true) {
    push({ code: 'FORMULA_NEVER_REQUIRED', message: 'A formula field cannot be required. Nobody fills it.', path });
  }
  // A formula may read another worked-out field, on its row or through a hop
  // (9.3 of docs/plans/field-relations.md): the engine works them out in
  // order, and a ring is refused by the field graph (specGraphProblems).
  for (const problem of checkFormula(read, specFormulaLookup(spec, record, { formulaResults: true }), field.key)) {
    push({ code: problem.code, message: problem.message, path });
  }
  // Money on a list is worked out from records (W11 of
  // docs/plans/workbook-map.md): a money formula there reads only the totals
  // and formulas of its own row (Pending = Billed - Received), never a typed
  // field or a hop.
  if (record.kind === 'directory' && read.result === 'money') {
    const fieldOf = (key: string) => {
      const one = (Array.isArray(record.fields) ? record.fields : []).filter(isObject).find((held) => held.key === key);
      if (one === undefined || typeof one.type !== 'string') return null;
      return { type: one.type, formula: one.type === 'formula' ? formulaOf(readFieldSpec(one.spec)) : null };
    };
    if (!readsOnlyWorkedOut(read, fieldOf)) {
      push({
        code: 'DIRECTORY_HOLDS_MONEY',
        message: 'A list works money out only from the totals and formulas of its own row. Money that is typed lives on a record.',
        path,
      });
    }
  }
  return read;
};

/**
 * What the field starts with, checked with the same key lookup `spec` is.
 *
 * `defaultProblems` owns every rule — which types take a default at all, that a
 * default never reads a worked-out field, and that the answer fits the field —
 * so a template and a person editing a record are held to one standard. The
 * only thing this adds is the spec's own vocabulary: the choices of a select
 * are its option KEYS and the list a reference points at is a record KEY, which
 * is what `optionsOf` and `targetOf` answer above.
 */
/**
 * "The open row for this vehicle" (step 9.2), by keys: `by` a reference of
 * this record, `match` a reference of the target to the same list, the status
 * a select of the target and its open choices keys it holds.
 */
const checkOpenRow = (
  field: SpecField,
  spec: SetupSpec,
  record: SpecRecordOrLines,
  path: string,
  push: (p: SpecProblem | null) => void,
): void => {
  if (field.openRow === undefined) return;
  const rule = readOpenRow(field.openRow);
  if (rule === null) {
    push({ code: 'OPEN_ROW_SHAPE', message: 'The open row a field starts with names the field it is found by and the field that matches it.', path });
    return;
  }
  const asCheck = (one: SpecField): { id: string; type: string; target: string | null } => ({ id: one.key, type: one.type, target: one.targetRecordKey ?? null });
  const own = (Array.isArray(record.fields) ? record.fields : []).filter(isObject).map(asCheck);
  const targetFields = fieldsOfRecord(spec, field.targetRecordKey);
  for (const message of openRowProblems(rule, asCheck(field), own, targetFields.map(asCheck))) push({ code: 'OPEN_ROW_SHAPE', message, path });
  if (rule.status !== undefined) {
    const status = targetFields.find((one) => one.key === rule.status!.field);
    const keys = new Set((status?.options ?? []).map((option) => option.key));
    for (const open of rule.status.open) {
      if (status !== undefined && !keys.has(open)) push({ code: 'OPEN_ROW_SHAPE', message: `"${open}" is not a choice of "${rule.status.field}".`, path });
    }
  }
};

const checkStartsWith = (
  field: SpecField,
  spec: SetupSpec,
  record: SpecRecordOrLines,
  path: string,
  push: (p: SpecProblem | null) => void,
): void => {
  if (field.startsOnce !== undefined && typeof field.startsOnce !== 'boolean') {
    push({ code: 'FORMULA_SHAPE', message: 'Whether a starting value fills once is a yes or a no.', path });
  }
  if (field.startsWith === undefined) {
    if (field.startsOnce === true) {
      push({ code: 'STARTS_ONCE_ALONE', message: 'Only a field that starts with a value can fill it once.', path });
    }
    return;
  }
  const read = readFormulaSpec(field.startsWith);
  if (read === null) {
    push({ code: 'FORMULA_SHAPE', message: 'What the field starts with is not a tree this version can read.', path });
    return;
  }
  const once = field.startsOnce === true;
  const problems = defaultProblems(field.type, read, specFormulaLookup(spec, record, { formulaResults: once }), field.key, {
    optionIds: field.options?.map((option) => option.key) ?? null,
    targetRecordId: field.targetRecordKey ?? null,
    once,
    recordId: record.key,
  });
  for (const problem of problems) {
    push({ code: problem.code, message: problem.message, path });
  }
};

/**
 * When the field shows, held to `showWhenProblems`, the rule the planner holds
 * a person's own edit to: a yes or a no, read from typed fields on the row and
 * one hop, and never a total over lines.
 */
const checkShowWhen = (
  field: SpecField,
  spec: SetupSpec,
  record: SpecRecordOrLines,
  path: string,
  push: (p: SpecProblem | null) => void,
): void => {
  if (field.showWhen === undefined) return;
  const read = readFormulaSpec(field.showWhen);
  if (read === null) {
    push({ code: 'FORMULA_SHAPE', message: 'When the field shows is not a tree this version can read.', path });
    return;
  }
  for (const problem of showWhenProblems(read, specFormulaLookup(spec, record), field.key)) {
    push({ code: problem.code, message: problem.message, path });
  }
};

/**
 * When a person may change the field, held to `editableWhenProblems`, the rule
 * the planner holds a person's own edit to (F10 of
 * docs/plans/field-relations.md): read like a show-when rule.
 */
const checkEditableWhen = (
  field: SpecField,
  spec: SetupSpec,
  record: SpecRecordOrLines,
  path: string,
  push: (p: SpecProblem | null) => void,
): void => {
  if (field.editableWhen === undefined) return;
  const read = readFormulaSpec(field.editableWhen);
  if (read === null) {
    push({ code: 'FORMULA_SHAPE', message: 'When the field can be changed is not a tree this version can read.', path });
    return;
  }
  for (const problem of editableWhenProblems(read, specFormulaLookup(spec, record), field.key)) {
    push({ code: problem.code, message: problem.message, path });
  }
};

/**
 * Must be rules (F13 of docs/plans/field-relations.md), on a field or on the
 * whole record, held to `mustBeProblems`, the rule the structure planner holds
 * a person's own rule to: a yes-or-no tree that may read anything on the row,
 * worked-out fields and lines included, a message of 1 to 200 letters whose
 * braces name a field of the record (by key or by label), and a level.
 */
const checkMustBeRules = (
  rules: unknown,
  most: number,
  spec: SetupSpec,
  record: SpecRecordOrLines,
  catalogue: Catalogue,
  path: string,
  push: (p: SpecProblem | null) => void,
): void => {
  if (rules === undefined) return;
  if (!Array.isArray(rules) || rules.length > most) {
    push({ code: 'MUST_BE_SHAPE', message: `A field or a record holds at most ${most} rules, each a rule, its words and a level.`, path });
    return;
  }
  const lookup = specFormulaLookup(spec, record, { formulaResults: true });
  const names = record.fields.flatMap((field) => [field.key, ...(catalogue[field.labelKey] === undefined ? [] : [catalogue[field.labelKey]!])]);
  rules.forEach((raw, index) => {
    const at = `${path}[${index}]`;
    const rule = readMustBeRule(raw);
    if (rule === null) {
      push({ code: 'MUST_BE_SHAPE', message: 'A rule is a yes-or-no tree, the words shown when it says no, and block or warn.', path: at });
      return;
    }
    for (const problem of mustBeProblems(rule, lookup, null, names)) push({ code: problem.code, message: problem.message, path: at });
  });
};

/**
 * A `lines` field: the rules the structure planner makes of a lines add, made
 * of a spec. The lines record it holds is checked as a record of its own,
 * beside the spec's records (`validateSpec`).
 */
const checkLinesField = (
  field: SpecField,
  record: SpecRecordOrLines,
  path: string,
  push: (p: SpecProblem | null) => void,
): void => {
  const lines = field.lines as unknown;
  if (!isObject(lines) || typeof lines.key !== 'string' || !Array.isArray(lines.fields)) {
    push({ code: 'LINES_SHAPE', message: `A lines field needs { key, nameKey, fields: [...] }, the fields each line holds.`, path });
    return;
  }
  if (record.kind === 'directory') {
    push({ code: 'LINES_ON_LIST', message: 'A list holds no lines. Add lines to a record.', path });
  }
  if (depthOf(record) + 1 > LINES_MAX_DEPTH) {
    push({
      code: 'LINES_TOO_DEEP',
      message: `Lines go ${LINES_MAX_DEPTH} levels deep at most, so a line of a line holds no lines of its own.`,
      path,
    });
  }
  if (field.required === true) {
    push({ code: 'LINES_NEVER_REQUIRED', message: 'A lines field cannot be required: an entry may hold no lines.', path });
  }
  if (field.startsWith !== undefined) {
    push({ code: 'LINES_NO_DEFAULT', message: 'A lines field starts with no lines, so it takes no starting value.', path });
  }
  if (field.targetRecordKey !== undefined) {
    push({ code: 'TARGET_NOT_ALLOWED', message: 'A lines field names no list: its lines are a record of their own.', path });
  }
  const held = (lines.fields as unknown[]).filter(isObject);
  if (held.length === 0) {
    push({ code: 'LINES_FIELDS_EMPTY', message: `"${field.key}" needs at least one field on each line.`, path });
  }
  if (held.length > LINES_MAX_FIELDS) {
    push({ code: 'LINES_FIELDS_MAX', message: `A line holds ${LINES_MAX_FIELDS} fields at most, and "${field.key}" holds ${held.length}.`, path });
  }
  for (const line of held) {
    if (line.key === LINES_PARENT_KEY || line.key === LINES_ROOT_KEY) {
      push({
        code: 'LINES_KEY_RESERVED',
        message: `A line field cannot be keyed "${String(line.key)}": every line already reads the row above as "${LINES_PARENT_KEY}" and the entry at the top as "${LINES_ROOT_KEY}".`,
        path,
      });
    }
  }
  if (lines.countFromKey !== undefined) {
    const count = (Array.isArray(record.fields) ? record.fields : []).find(
      (one) => isObject(one) && one.key === lines.countFromKey,
    );
    if (count === undefined || count.type !== 'number') {
      push({
        code: 'LINES_COUNT_FROM',
        message: `The lines of "${field.key}" start from a number field on the same row, and "${String(lines.countFromKey)}" is not one.`,
        path,
      });
    }
  }
  if (
    lines.max !== undefined &&
    (typeof lines.max !== 'number' || !Number.isInteger(lines.max) || lines.max < 1 || lines.max > ENTRY_BATCH_MAX)
  ) {
    push({ code: 'LINES_MAX_SHAPE', message: `A lines field holds 1 to ${ENTRY_BATCH_MAX} lines.`, path });
  }
  if (
    lines.start !== undefined &&
    (!linesStartHolds(lines.start) || (typeof lines.max === 'number' && lines.start > lines.max))
  ) {
    push({
      code: 'LINES_START_SHAPE',
      message: `A new entry opens with 0 to ${LINES_START_MAX} empty lines, and never more than the field holds.`,
      path,
    });
  }
};

function checkField(
  field: SpecField,
  spec: SetupSpec,
  record: SpecRecordOrLines,
  catalogue: Catalogue,
  path: string,
  push: (p: SpecProblem | null) => void,
): void {
  push(resolveKey(catalogue, field.labelKey, path, 'field labelKey'));
  checkKeyBy(field.labelKeyBy, catalogue, path, 'field labelKeyBy', push);
  checkAnswerTags(field.whenAnswer, path, 'field whenAnswer', push);
  if (field.helpText !== undefined) {
    push(resolveKey(catalogue, field.helpText, path, 'field helpText'));
  }
  const formula = checkFormulaSpec(field, spec, record, path, push);
  checkStartsWith(field, spec, record, path, push);
  checkOpenRow(field, spec, record, path, push);
  checkShowWhen(field, spec, record, path, push);
  checkEditableWhen(field, spec, record, path, push);
  checkMustBeRules(field.mustBe, MUST_BE_MAX, spec, record, catalogue, `${path}.mustBe`, push);
  if (field.type === 'lines') {
    checkLinesField(field, record, path, push);
  } else if (field.lines !== undefined) {
    push({ code: 'LINES_NOT_ALLOWED', message: 'Only a lines field holds lines.', path });
  }

  // The registry rules: real type, real component, declared props, the right
  // storage for the home, the right slot family.
  for (const problem of validateFieldDefinition({
    type: field.type,
    component: field.component,
    props: field.props ?? null,
    storage: field.storage,
    slot: field.slot ?? null,
    attr_key: field.attrKey ?? null,
    required: field.required ?? false,
    target_record: field.targetRecordKey ?? null,
  })) {
    push({ code: problem.code, message: problem.message, path });
  }

  // `validateFieldDefinition` tolerates a missing target column, because it
  // also validates drafts. A shipped spec is final: the storage must land.
  if (field.storage === 'slot') {
    if (!field.slot) {
      push({ code: 'SLOT_MISSING', message: 'A slot field must name its slot.', path });
    } else if (isValueTypeId(field.type)) {
      // A formula has no fixed home: its own result names the family, the
      // rule `slotFamilyFor` holds and the planner follows. With no readable
      // recipe there is no family to check against, and the recipe itself has
      // already been refused above.
      const letter =
        field.type === 'formula'
          ? formula === null
            ? undefined
            : slotFamilyFor(formula.result)
          : field.type === 'rollup'
            ? rollupSlotLetter(field, spec)
            : SLOT_LETTER_FOR_HOME[VALUE_TYPES[field.type].home];
      // The physical columns (`ENTRY_SLOT_COLUMNS`), which `validateFieldDefinition`
      // cannot know because it checks one field with no table in sight.
      if (letter !== undefined && !(ENTRY_SLOT_COLUMNS.has(field.slot) && field.slot.startsWith(letter))) {
        push({
          code: 'SLOT_UNKNOWN',
          message: `Slot "${field.slot}" is not a ${letter} column data.entry holds.`,
          path,
        });
      }
    }
  }
  if (field.storage === 'attr') {
    if (!field.attrKey) {
      push({ code: 'ATTR_KEY_MISSING', message: 'An attr field must carry an attrKey.', path });
    } else if (!KEY_PATTERN.test(field.attrKey) || field.attrKey.length > 12) {
      // The key repeats in every row that fills the field, so it stays short.
      push({
        code: 'ATTR_KEY_INVALID',
        message: `attrKey "${field.attrKey}" must be short snake_case, 12 characters at most.`,
        path,
      });
    }
  }

  // Options belong to a select and to nothing else. A directory of rows, not
  // options, answers every open-ended list.
  if (field.type === 'select') {
    if (!field.options || field.options.length < 2) {
      push({
        code: 'OPTIONS_MISSING',
        message: 'A select field must declare at least two options.',
        path,
      });
    }
  } else if (field.options !== undefined) {
    push({
      code: 'OPTIONS_UNEXPECTED',
      message: `A "${field.type}" field takes no options. Use a directory and a reference.`,
      path,
    });
  }
  const optionKeys = new Set<string>();
  for (const option of (Array.isArray(field.options) ? field.options : []).filter(isObject)) {
    push(resolveKey(catalogue, option.labelKey, path, `option "${option.key}" labelKey`));
    if (optionKeys.has(option.key)) {
      push({ code: 'OPTION_KEY_DUP', message: `Option key "${option.key}" repeats.`, path });
    }
    optionKeys.add(option.key);
    // The colour is a closed word, not a hex: a device maps it to its own
    // feedback tone, and a fifth word would draw nothing at all.
    if (option.tone !== undefined && !isCaptureTone(option.tone)) {
      push({
        code: 'OPTION_TONE',
        message: `Option "${option.key}" wears tone "${String(option.tone)}", which is not positive, notice, negative or information.`,
        path,
      });
    }
  }

  // A prop that names a catalogue key must resolve like any label does.
  for (const [prop, value] of Object.entries(field.props ?? {})) {
    if (prop.endsWith('_key') && typeof value === 'string') {
      push(resolveKey(catalogue, value, path, `prop "${prop}"`));
    }
  }

  if (field.targetRecordKey !== undefined) {
    const target = record.key === field.targetRecordKey
      ? record
      : undefined;
    // Cross-record existence is checked by the caller, which sees the whole
    // spec. Here only the self-reference is knowable. A `reference` may pick
    // another entry of its own record (B3 of docs/plans/gym-membership.md: a
    // membership's Previous membership, which starts with the member's latest
    // one); a multi_select reads a list's rows, never its own record's.
    if (target && field.type !== 'reference') {
      push({
        code: 'REFERENCE_TARGET_SELF',
        message: 'A reference field cannot point at its own record.',
        path,
      });
    }
  }
}

/** Every field key one record's capture names. Empty when it has none. */
const captureFieldKeys = (record: SpecRecordOrLines): Set<string> => {
  const held = record.capture as Record<string, unknown> | undefined;
  if (!isObject(held)) return new Set();
  const keys = [
    held.overFieldKey,
    held.cellFieldKey,
    ...(Array.isArray(held.sharedFieldKeys) ? held.sharedFieldKeys : []),
  ];
  return new Set(keys.filter((key): key is string => typeof key === 'string'));
};

/**
 * How a record's entries are written, checked against the record's own keys.
 *
 * The same three rules the server's planner makes of a live record, made here
 * of a spec that has no ids yet: the sheet draws one line per row of a LIST, a
 * mark is one tap on a fixed set of WORDS, and the day is asked once at the top
 * rather than once per line. A key stands in for an id on both sides, which is
 * exactly what apply rewrites.
 *
 * The shape is read defensively rather than trusted, like every other block
 * here: a spec is JSON on disk, and the type on it is a promise about what we
 * meant to write, not about what is there.
 */
const checkCapture = (
  record: SpecRecordOrLines,
  fieldKeys: ReadonlyMap<string, SpecField>,
  at: string,
  push: (p: SpecProblem | null) => void,
): void => {
  if (record.capture === undefined) return;
  const path = `${at}.capture`;
  const held = record.capture as Record<string, unknown>;
  if (!isObject(held)) {
    push({ code: 'CAPTURE_NOT_OBJECT', message: 'A capture setting must be an object.', path });
    return;
  }
  if (held.mode !== 'sheet') {
    push({
      code: 'CAPTURE_MODE',
      message: `Capture mode "${String(held.mode)}" is not one this version writes. Only "sheet" is.`,
      path,
    });
  }

  /** One field of this record by key, or null with the refusal already pushed. */
  const named = (key: unknown, what: string): SpecField | null => {
    const field = typeof key === 'string' ? fieldKeys.get(key) : undefined;
    if (field === undefined) {
      push({
        code: 'CAPTURE_FIELD_UNKNOWN',
        message: `The capture's ${what} names field "${String(key)}", which the record does not declare.`,
        path,
      });
      return null;
    }
    return field;
  };

  const over = named(held.overFieldKey, 'over');
  if (over !== null && over.type !== 'reference') {
    push({
      code: 'CAPTURE_OVER_NOT_REFERENCE',
      message: `A sheet draws one line per row of a list, so "${over.key}" must be a field that reads one, not a "${over.type}".`,
      path,
    });
  }

  const cell = named(held.cellFieldKey, 'cell');
  if (cell !== null && cell.type !== 'select') {
    push({
      code: 'CAPTURE_CELL_NOT_SELECT',
      message: `A mark is one tap on a fixed set of words, so "${cell.key}" must be a field of choices, not a "${cell.type}".`,
      path,
    });
  }

  // The day. A sheet is a sheet FOR a day, so the field the record reads as
  // its day is asked once at the top or the sheet cannot be written at all.
  const day = (Array.isArray(record.bindings) ? record.bindings : [])
    .filter(isObject)
    .find((binding) => binding.purpose === 'time_axis')?.fieldKey;
  if (!Array.isArray(held.sharedFieldKeys)) {
    push({
      code: 'CAPTURE_SHARED_INVALID',
      message: "The capture's shared fields must be a list of field keys.",
      path,
    });
  } else {
    const shared = held.sharedFieldKeys as unknown[];
    if (day === undefined || !shared.includes(day)) {
      push({
        code: 'CAPTURE_SHARED_INVALID',
        message:
          day === undefined
            ? 'A sheet is written for one day, and this record reads no field as its day.'
            : `A sheet is written for one day, so the shared fields must hold "${day}".`,
        path,
      });
    }
    for (const key of shared) {
      if (key === day) continue;
      if (key === held.overFieldKey || key === held.cellFieldKey) {
        push({
          code: 'CAPTURE_SHARED_INVALID',
          message: `"${String(key)}" is already the sheet's ${key === held.overFieldKey ? 'list' : 'mark'}, so it cannot also be asked once at the top.`,
          path,
        });
        continue;
      }
      const field = named(key, 'shared fields');
      if (field !== null && field.type !== 'reference') {
        push({
          code: 'CAPTURE_SHARED_INVALID',
          message: `"${field.key}" is a "${field.type}". Beyond the day, a sheet asks only fields that read a list once at the top.`,
          path,
        });
      }
    }
  }

  if (held.quickOptionKey !== undefined) {
    const offered = (cell?.options ?? []).some(
      (option) => isObject(option) && option.key === held.quickOptionKey,
    );
    if (!offered) {
      push({
        code: 'CAPTURE_QUICK_UNKNOWN',
        message: `The one-tap shortcut fills "${String(held.quickOptionKey)}", which "${String(held.cellFieldKey)}" does not offer.`,
        path,
      });
    }
  }
};

function checkRecord(
  spec: SetupSpec,
  record: SpecRecordOrLines,
  catalogue: Catalogue,
  options: SpecValidateOptions,
  push: (p: SpecProblem | null) => void,
  /** A lines record, written out of the `lines` field that holds it. */
  nested = false,
): void {
  const lines = nested ? (record as LinesRecordSpec) : null;
  // A lines record's problems are reported under the field that holds it, so a
  // repair reaches the record a person (or a model) actually wrote.
  const at = lines === null ? `${spec.id}.records[${record.key}]` : lines.path;
  push(resolveKey(catalogue, record.nameKey, at, 'record nameKey'));
  if (lines !== null && lines.lines.nounKeys !== undefined) {
    const nouns = lines.lines.nounKeys as unknown;
    if (!isObject(nouns)) {
      push({ code: 'LINES_SHAPE', message: 'The words for a line are { one, other }.', path: at });
    } else {
      push(resolveKey(catalogue, nouns.one, at, 'line noun'));
      push(resolveKey(catalogue, nouns.other, at, 'line nouns'));
    }
  }
  checkKeyBy(record.nameKeyBy, catalogue, at, 'record nameKeyBy', push);
  checkAnswerTags(record.whenAnswer, at, 'record whenAnswer', push);
  if (record.whenAnswer !== undefined && record.kind !== 'directory') {
    push({
      code: 'RECORD_WHEN_ANSWER_NOT_DIRECTORY',
      message: 'Only a directory may depend on an answer. Dropping the record people write into would leave nothing to write.',
      path: at,
    });
  }
  if (!KEY_PATTERN.test(record.key ?? '')) {
    push({ code: 'RECORD_KEY_INVALID', message: `Record key "${record.key}" is not snake_case.`, path: at });
  }
  if (record.kind !== 'record' && record.kind !== 'directory' && !(nested && record.kind === 'lines')) {
    push({
      code: 'RECORD_KIND_INVALID',
      message:
        record.kind === 'lines'
          ? 'A lines record is written inside the lines field that holds it, never as a record of the spec.'
          : `Record kind "${record.kind}" is not record or directory.`,
      path: at,
    });
  }
  // What the record holds, and so what its glyph draws. A closed list of
  // twenty-three words, and a word off it is a build failure rather than a
  // record wearing an icon no device holds. Absent is the default glyph.
  if (record.icon !== undefined && !isRecordIconId(record.icon)) {
    push({
      code: 'ICON_UNKNOWN',
      message: `Icon "${String(record.icon)}" is not one of the names a record may hold. See RECORD_ICON_IDS.`,
      path: at,
    });
  }

  const fields = Array.isArray(record.fields) ? record.fields : [];
  if (!Array.isArray(record.fields) || fields.length === 0) {
    push({ code: 'FIELDS_MISSING', message: 'A record must declare at least one field.', path: at });
  }

  const fieldKeys = new Map<string, SpecField>();
  // Each label's first field, said without case or the spaces round it: app.field
  // holds one live field per (record_id, lower(label)) (`field_label_unique`).
  const labels = new Map<string, string>();
  const slots = new Set<string>();
  const attrKeys = new Set<string>();
  const ords = new Set<number>();

  for (const field of fields) {
    if (!isObject(field)) {
      push({ code: 'FIELD_NOT_OBJECT', message: 'A field must be an object.', path: at });
      continue;
    }
    const path = `${at}.fields[${field.key}]`;
    if (!KEY_PATTERN.test(field.key ?? '')) {
      push({ code: 'FIELD_KEY_INVALID', message: `Field key "${field.key}" is not snake_case.`, path });
    }
    if (fieldKeys.has(field.key)) {
      push({ code: 'FIELD_KEY_DUP', message: `Field key "${field.key}" repeats in one record.`, path });
    }
    fieldKeys.set(field.key, field);
    // Two Items tables on one Orders (session cbde5e49, 19 Oct): a stage drew
    // the held `lines` again as `items`, and Build died on the index.
    const label = typeof field.labelKey === 'string' ? catalogue[field.labelKey]?.trim() : undefined;
    if (label !== undefined && label !== '') {
      const first = labels.get(label.toLowerCase());
      if (first === undefined) labels.set(label.toLowerCase(), field.key);
      else if (first !== field.key) {
        push({
          code: 'FIELD_LABEL_DUP',
          message: `Fields "${first}" and "${field.key}" are both called "${label}" in one record. To change "${first}", write it under its own key "${first}"; otherwise give "${field.key}" a name of its own.`,
          path,
        });
      }
    }

    const customAllowed = options.allowCustomTier === true && field.tier === 'custom';
    if (field.tier !== 'primary' && field.tier !== 'secondary' && !customAllowed) {
      push({
        code: 'TIER_INVALID',
        message: `Tier "${field.tier}" is not primary or secondary. A template never ships a custom field.`,
        path,
      });
    }

    checkField(field, spec, record, catalogue, path, push);

    // One field owns one slot, the unique index in migration 0001 as a build
    // check. Same for an attr key, which is never reused.
    if (field.slot) {
      if (slots.has(field.slot)) {
        push({ code: 'SLOT_TAKEN', message: `Slot "${field.slot}" is claimed twice in one record.`, path });
      }
      slots.add(field.slot);
    }
    if (field.attrKey) {
      if (attrKeys.has(field.attrKey)) {
        push({ code: 'ATTR_KEY_TAKEN', message: `attrKey "${field.attrKey}" is claimed twice in one record.`, path });
      }
      attrKeys.add(field.attrKey);
    }
    if (!Number.isInteger(field.ord)) {
      push({ code: 'ORD_INVALID', message: 'ord must be an integer.', path });
    } else if (ords.has(field.ord)) {
      push({ code: 'ORD_TAKEN', message: `ord ${field.ord} repeats in one record.`, path });
    }
    ords.add(field.ord);

    // The database rule record_directory_holds_no_currency, one level up: the
    // record would need a currency, and a directory never carries one.
    if (record.kind === 'directory' && field.type === 'money') {
      push({
        code: 'DIRECTORY_HOLDS_MONEY',
        message: 'A directory declares no currency field. Money lives on entries, not on rows a picker lists.',
        path,
      });
    }

    // A lines record's parent and root point up its own tree, at a record or a
    // line, and are written by `linesRecordsOf`; every other field that reads
    // a list reads a list of the spec, never a lines record.
    if ((field.type === 'reference' || field.type === 'multi_select') && !isTreeField(record, field.key)) {
      const target = (Array.isArray(spec.records) ? spec.records : [])
        .find((r) => isObject(r) && r.key === field.targetRecordKey);
      if (!target) {
        push({
          code: 'REFERENCE_TARGET_UNKNOWN',
          message: `targetRecordKey "${field.targetRecordKey}" names no record in this spec.`,
          path,
        });
      } else if (target.kind === 'record' && field.type === 'reference') {
        // A reference may pick one entry of another record (a Guest Service
        // Order picks its Booking). The picker and every read of the pick
        // name the entry by its title as text, so a record whose title is a
        // choice or a pick of its own cannot be picked: it would read as an id.
        const title = pickTitleOf(target);
        // An entry of the record itself (B3 of docs/plans/gym-membership.md) titled by a pick is named
        // through that pick, one step on (`resolveReferences` on the server, `titlesForEntryIds` on a device):
        // a membership by its member. Another record's entry titled so is still refused.
        const selfByPick = target.key === record.key && title === 'reference';
        if (title !== null && !TEXT_TITLE_TYPES.has(title) && !selfByPick) {
          push({
            code: 'REFERENCE_TARGET_TITLE_NOT_TEXT',
            // The way on, for the stage that repairs it (Hotel Lakeview, 30 Oct: a room charge's Booking was dropped and noted as not possible).
            message: `"${field.targetRecordKey}" is named by a ${title === 'select' ? 'choice' : 'pick'}, not by words, so an entry of it cannot be picked by name: head "${field.targetRecordKey}" by a field of words typed on it (the guest's name on a booking), or pick what its title picks instead.`,
            path,
          });
        }
      } else if (target.kind !== 'directory') {
        push({
          code: 'REFERENCE_TARGET_NOT_DIRECTORY',
          message:
            field.type === 'reference'
              ? `targetRecordKey "${field.targetRecordKey}" must name a directory or a record.`
              : `targetRecordKey "${field.targetRecordKey}" must name a directory.`,
          path,
        });
      }
    }
  }

  // Bindings: the registry rule per binding, plus what only the record knows,
  // that the field exists, that a purpose is answered once, and that a select
  // answering `sign` says which choice counts which way.
  const purposes = new Set<string>();
  for (const binding of Array.isArray(record.bindings) ? record.bindings : []) {
    if (!isObject(binding)) {
      push({ code: 'BINDING_NOT_OBJECT', message: 'A binding must be an object.', path: at });
      continue;
    }
    const path = `${at}.bindings[${binding.purpose}]`;
    if (purposes.has(binding.purpose)) {
      push({ code: 'BINDING_PURPOSE_DUP', message: `Purpose "${binding.purpose}" is bound twice.`, path });
    }
    purposes.add(binding.purpose);

    const field = binding.fieldKey === undefined ? undefined : fieldKeys.get(binding.fieldKey);
    if (binding.fieldKey !== undefined && !field) {
      push({
        code: 'BINDING_FIELD_UNKNOWN',
        message: `Binding "${binding.purpose}" names field "${binding.fieldKey}", which the record does not declare.`,
        path,
      });
      continue;
    }
    for (const problem of validateBinding({
      purpose: binding.purpose,
      field_type: field?.type ?? null,
      const_num: binding.constNum ?? null,
    })) {
      push({ code: problem.code, message: problem.message, path });
    }
    if (binding.purpose === 'sign' && field?.type === 'select') {
      for (const option of (Array.isArray(field.options) ? field.options : []).filter(isObject)) {
        if (option.sign !== 1 && option.sign !== -1) {
          push({
            code: 'SIGN_OPTION_MISSING',
            message: `Option "${option.key}" of the sign field "${field.key}" carries no sign. Every choice must count 1 or -1.`,
            path,
          });
        }
      }
    }
  }
  // A line is named by the field that holds it, never on its own.
  if (!purposes.has('title') && record.kind !== 'lines') {
    push({
      code: 'BINDING_TITLE_MISSING',
      message: 'Every record binds a title, or its rows have no name.',
      path: at,
    });
  }

  // Views. The spec body is the closed vocabulary of app.view.spec and stays
  // opaque here, except the one part that carries words: summary tile labels.
  const viewOrds = new Set<number>();
  const viewTypes = new Set<string>();
  for (const view of Array.isArray(record.views) ? record.views : []) {
    if (!isObject(view)) {
      push({ code: 'VIEW_NOT_OBJECT', message: 'A view must be an object.', path: at });
      continue;
    }
    const path = `${at}.views[${view.nameKey}]`;
    push(resolveKey(catalogue, view.nameKey, path, 'view nameKey'));
    if (view.type !== 'list' && view.type !== 'summary') {
      push({ code: 'VIEW_TYPE_INVALID', message: `View type "${view.type}" is not list or summary.`, path });
    }
    viewTypes.add(view.type);
    if (viewOrds.has(view.ord)) {
      push({ code: 'VIEW_ORD_TAKEN', message: `View ord ${view.ord} repeats.`, path });
    }
    viewOrds.add(view.ord);
    const summaries = (view.spec as { summaries?: unknown }).summaries;
    for (const tile of Array.isArray(summaries) ? summaries : []) {
      const labelKey = (tile as { label_key?: unknown }).label_key;
      push(resolveKey(catalogue, labelKey, path, 'summary tile label_key'));
      for (const problem of summaryTileProblems(tile, record, path)) push(problem);
    }
  }
  if (record.kind === 'record' && (!viewTypes.has('list') || !viewTypes.has('summary'))) {
    push({
      code: 'VIEW_MISSING',
      message: 'A record ships one list view and one summary view at least.',
      path: at,
    });
  }

  // A field an answer can drop must be droppable. Required means the form
  // could never be saved without it; bound means a purpose would point at
  // nothing; named by a view means a stored tile would point at nothing.
  const boundKeys = new Set(
    (Array.isArray(record.bindings) ? record.bindings : [])
      .filter(isObject)
      .map((binding) => binding.fieldKey)
      .filter((key): key is string => typeof key === 'string'),
  );
  const namedByViews = viewFieldKeys(record);
  const namedByCapture = captureFieldKeys(record);
  for (const [key, field] of fieldKeys) {
    if (field.whenAnswer === undefined) continue;
    const path = `${at}.fields[${key}]`;
    if (field.required === true) {
      push({
        code: 'WHEN_ANSWER_ON_REQUIRED',
        message: `Field "${key}" is required, so it can never be dropped by an answer.`,
        path,
      });
    }
    if (boundKeys.has(key)) {
      push({
        code: 'WHEN_ANSWER_ON_BOUND',
        message: `Field "${key}" answers a purpose, so dropping it would leave the binding pointing at nothing.`,
        path,
      });
    }
    if (namedByViews.has(key)) {
      push({
        code: 'WHEN_ANSWER_ON_VIEW_FIELD',
        message: `Field "${key}" is named by a view of this record, so dropping it would leave that view pointing at nothing.`,
        path,
      });
    }
    if (namedByCapture.has(key)) {
      push({
        code: 'WHEN_ANSWER_ON_CAPTURE',
        message: `Field "${key}" is part of how this record's entries are written, so dropping it would leave the sheet with no list, no mark, or no day.`,
        path,
      });
    }
  }

  checkCapture(record, fieldKeys, at, push);
  if (lines === null) checkMustBeRules((record as SpecRecord).rules, RECORD_RULES_MAX, spec, record, catalogue, `${at}.rules`, push);

  // Seed rows: every column is a declared field, every string is a key. The
  // conditional rows carry the same rule, plus real answer tags.
  const checkSeedValues = (row: unknown, path: string): void => {
    for (const [key, value] of Object.entries(isObject(row) ? row : {})) {
      if (!fieldKeys.has(key)) {
        push({ code: 'SEED_FIELD_UNKNOWN', message: `Seed row names field "${key}", which the record does not declare.`, path });
      } else if (fieldKeys.get(key)!.whenAnswer !== undefined) {
        push({
          code: 'SEED_FIELD_CONDITIONAL',
          message: `Seed row names field "${key}", which an answer can drop. The row would carry a value for a field that was never created.`,
          path,
        });
      }
      if (typeof value === 'string') {
        push(resolveKey(catalogue, value, path, `seed value for "${key}"`));
      }
      const field = fieldKeys.get(key);
      if (field?.type === 'multi_select') {
        push({
          code: 'SEED_VALUE_SET',
          message: `Seed row fills "${key}", which holds several rows. A seed row cannot choose them; leave it out.`,
          path,
        });
      } else if (field?.type === 'reference') {
        checkSeedReference(spec, catalogue, field, value, path, push);
      }
    }
  };

  for (const [index, row] of (Array.isArray(record.seedRows) ? record.seedRows : []).entries()) {
    checkSeedValues(row, `${at}.seedRows[${index}]`);
  }

  for (const [index, row] of (Array.isArray(record.seedRowsWhen) ? record.seedRowsWhen : []).entries()) {
    const path = `${at}.seedRowsWhen[${index}]`;
    if (!isObject(row)) {
      push({ code: 'SEED_WHEN_NOT_OBJECT', message: 'A conditional seed row must be an object.', path });
      continue;
    }
    checkAnswerTags(row.answerTags, path, 'seedRowsWhen answerTags', push);
    if (!isObject(row.values)) {
      push({ code: 'SEED_WHEN_VALUES_MISSING', message: 'A conditional seed row must carry `values`.', path });
      continue;
    }
    checkSeedValues(row.values, path);
  }

  if (record.kind !== 'directory' && (record.seedRows !== undefined || record.seedRowsWhen !== undefined)) {
    push({
      code: 'SEED_ON_RECORD',
      message: 'Only a directory is seeded. A row in a record people read must be one a person wrote.',
      path: at,
    });
  }

  // Where each field shows: the tree `PATCH /structure` would write, keyed by
  // this record's field keys, read by the reader every device runs.
  if (record.layout !== undefined) {
    const judged = judgeLayout(record.layout, fieldKeys.keys());
    if (!judged.ok) {
      push({
        code: judged.code,
        message:
          judged.fieldId === null
            ? 'The form\'s sections are not a tree this version can draw: sections inside sections go two levels at most.'
            : `The form's sections name "${judged.fieldId}" ${judged.code === 'LAYOUT_FIELD_TWICE' ? 'twice. A field shows once.' : ', which the record does not declare.'}`,
        path: `${at}.layout`,
      });
    }
  }
}

/**
 * A seed row's value for a reference field: the word of a row the target
 * list seeds, which apply turns into the id it mints for that row
 * (seed-references.ts). Only the target's always rows count, because a row an
 * answer can drop may not be there to point at.
 */
const checkSeedReference = (
  spec: SetupSpec,
  catalogue: Catalogue,
  field: SpecField,
  value: unknown,
  path: string,
  push: (p: SpecProblem | null) => void,
): void => {
  const target = (Array.isArray(spec.records) ? spec.records : [])
    .filter(isObject)
    .find((one) => one.key === field.targetRecordKey);
  // An unknown target is refused where the field is checked.
  if (target === undefined) return;
  const titleKey = titleFieldKeyOf(target);
  const word = typeof value === 'string' ? catalogue[value] : undefined;
  const words = (Array.isArray(target.seedRows) ? target.seedRows : []).flatMap((row) => {
    const held = titleKey === undefined ? undefined : row?.[titleKey];
    const said = typeof held === 'string' ? catalogue[held] : undefined;
    return typeof said === 'string' ? [said] : [];
  });
  if (typeof word !== 'string' || !words.some((one) => seedWordsMatch(one, word))) {
    push({
      code: 'SEED_REFERENCE_UNKNOWN',
      message: `Seed row fills "${field.key}" with "${typeof word === 'string' ? word : String(value)}", which is not the name of a row "${target.key}" seeds. Name one of: ${words.map((one) => `"${one}"`).join(', ') || '(it seeds none)'}.`,
      path,
    });
  }
};

/**
 * Every problem with one template spec against one catalogue. An empty array
 * means the spec may ship. Never throws, whatever the input holds.
 *
 * With no `options` the rules are the JSON pack's, unchanged. See
 * `SpecValidateOptions` for the one thing a caller may relax.
 */
export function validateSpec(
  spec: SetupSpec,
  catalogue: Catalogue,
  options: SpecValidateOptions = {},
): SpecProblem[] {
  const problems: SpecProblem[] = [];
  const push = (p: SpecProblem | null): void => void (p && problems.push(p));

  if (typeof spec !== 'object' || spec === null) {
    return [{ code: 'SPEC_NOT_OBJECT', message: 'The spec is not an object.', path: '(root)' }];
  }
  const at = String(spec.id ?? '(no id)');
  push(resolveKey(catalogue, spec.labelKey, at, 'labelKey'));
  push(resolveKey(catalogue, spec.descriptionKey, at, 'descriptionKey'));
  push(resolveKey(catalogue, spec.nounKey, at, 'nounKey'));
  push(resolveKey(catalogue, spec.entryNounKey, at, 'entryNounKey'));
  checkKeyBy(spec.nounKeyBy, catalogue, at, 'nounKeyBy', push);
  checkKeyBy(spec.entryNounKeyBy, catalogue, at, 'entryNounKeyBy', push);

  const records = Array.isArray(spec.records) ? spec.records : [];
  if (records.length === 0) {
    push({ code: 'RECORDS_MISSING', message: 'A template must declare at least one record.', path: at });
    return problems;
  }
  const recordKeys = new Set<string>();
  for (const record of records) {
    if (!isObject(record)) {
      push({ code: 'RECORD_NOT_OBJECT', message: 'A record must be an object.', path: at });
      continue;
    }
    if (recordKeys.has(record.key)) {
      push({ code: 'RECORD_KEY_DUP', message: `Record key "${record.key}" repeats.`, path: `${at}.records[${record.key}]` });
    }
    recordKeys.add(record.key);
    checkRecord(spec, record, catalogue, options, push);
  }
  // Every lines record, as a record of its own, keyed in the same key space.
  for (const lines of recordsOf(spec).filter((record): record is LinesRecordSpec => record.kind === 'lines' && 'path' in record)) {
    if (recordKeys.has(lines.key)) {
      push({ code: 'RECORD_KEY_DUP', message: `Record key "${lines.key}" repeats.`, path: lines.path });
    }
    recordKeys.add(lines.key);
    checkRecord(spec, lines, catalogue, options, push, true);
  }

  // The money rules and the Accounts list (step 2.3 of docs/plans/ledger.md): a
  // rule the server could not post by is refused here, before any row exists.
  const ruled = records.filter(isObject) as SpecRecord[];
  const accountsLists = ruled.filter((record) => record.accounts !== undefined);
  if (accountsLists.length > 1) {
    push({ code: 'ACCOUNTS_LIST_TWICE', message: 'A workspace keeps one Accounts list.', path: at });
  }
  const accountRows = new Set<string>();
  for (const list of accountsLists) {
    const title = list.fields.find((field) => field.key === titleFieldKeyOf(list));
    for (const row of list.seedRows ?? []) {
      const said = title === undefined ? undefined : row[title.key];
      if (typeof said === 'string') accountRows.add((catalogue[said] ?? said).trim().toLowerCase());
    }
    for (const found of accountsListProblems(list as RuleRecord, list.seedAccounts ?? [], `${at}.records[${list.key}]`)) push(found);
    // Each row is an account of one type: Build makes no account without it (30 Oct, a gym's "Kiro" and "Wifi").
    const typeKey = list.accounts?.typeFieldKey;
    const untyped = [...(list.seedRows ?? []), ...(list.seedRowsWhen ?? []).map((row) => row.values)].filter((row) => typeKey !== undefined && String(row[typeKey] ?? '').trim() === '');
    if (untyped.length > 0) {
      const names = untyped.map((row) => (title === undefined ? '' : String(row[title.key] ?? '').trim())).filter((name) => name !== '');
      const named = names.length === 0 ? `${untyped.length} of its rows` : names.map((name) => `"${catalogue[name] ?? name}"`).join(', ');
      push({
        code: 'ACCOUNTS_ROW_TYPE',
        message: `The Accounts list does not say what ${named} ${untyped.length === 1 ? 'is' : 'are'}: money you have, money you owe, owner's money, income or an expense. Say which, or take ${untyped.length === 1 ? 'it' : 'them'} out.`,
        path: `${at}.records[${list.key}]`,
      });
    }
    if ((list.seedAccounts ?? []).length > (list.seedRows ?? []).length) {
      push({ code: 'ACCOUNTS_SEED_ROWS', message: 'The Accounts list says more of its rows than it seeds.', path: `${at}.records[${list.key}]` });
    }
  }
  // The lists whose rows are stock items (Phase 9): a quantity rule reads their unit and pack.
  for (const record of ruled) {
    if (record.stockItems === undefined) continue;
    const mark = readSpecStockItems(record.stockItems);
    if (typeof mark === 'string') {
      push({ code: 'STOCK_ITEMS_SHAPE', message: `The stock items mark needs ${mark}.`, path: `${at}.records[${record.key}].stockItems` });
      continue;
    }
    for (const found of stockItemsProblems(mark, record as RuleRecord, `${at}.records[${record.key}].stockItems`)) push(found);
  }
  for (const record of ruled) {
    if (record.posting === undefined) continue;
    // A row is judged against the rows the list seeds. An Accounts list that seeds none (Edit the setup
    // reads it back without its rows, which stay in the workspace) leaves that check to the build.
    const rowsSeeded = accountsLists.some((list) => (list.seedRows ?? []).length > 0);
    for (const found of ruleProblems(record.posting, record as RuleRecord, ruled as RuleRecord[], `${at}.records[${record.key}].posting`, rowsSeeded ? accountRows : undefined)) push(found);
  }
  for (const record of ruled) {
    if (record.chargePlan === undefined) continue;
    const where = `${at}.records[${record.key}].chargePlan`;
    // A composed setup arrives as JSON: its plan's shape is read before its meaning.
    const plan = readSpecChargePlan(record.chargePlan);
    if (typeof plan === 'string') {
      push({ code: 'CHARGE_PLAN_SHAPE', message: `The charge plan needs ${plan}.`, path: where });
      continue;
    }
    for (const found of chargePlanProblems(plan, record as RuleRecord, ruled as RuleRecord[], where, accountsLists.length === 0 ? undefined : accountRows)) push(found);
  }
  // The approval chains a setup wrote (L55): read for their shape, then judged.
  for (const record of ruled) {
    if (record.approvalChain === undefined) continue;
    const where = `${at}.records[${record.key}].approvalChain`;
    const chain = readSpecApprovalChain(record.approvalChain);
    if (typeof chain === 'string') {
      push({ code: 'APPROVAL_CHAIN_SHAPE', message: `The approval chain needs ${chain}.`, path: where });
      continue;
    }
    for (const found of approvalChainProblems(chain, record as RuleRecord, where)) push(found);
  }
  // The task rules a setup wrote (step 9.3): read for their shape, then judged.
  for (const record of ruled) {
    const tasks = (record as { tasks?: unknown }).tasks;
    if (tasks === undefined) continue;
    const where = `${at}.records[${record.key}].tasks`;
    if (!Array.isArray(tasks) || tasks.length > TASK_RULES_MAX) {
      push({ code: 'TASK_RULE_SHAPE', message: `A record keeps up to ${TASK_RULES_MAX} task rules.`, path: where });
      continue;
    }
    for (const [index, raw] of tasks.entries()) {
      const rule = readSpecTaskRule(raw);
      if (typeof rule === 'string') {
        push({ code: 'TASK_RULE_SHAPE', message: `A task rule needs ${rule}.`, path: `${where}[${index}]` });
        continue;
      }
      for (const message of specTaskRuleProblems(rule, record as unknown as Parameters<typeof specTaskRuleProblems>[1])) push({ code: 'TASK_RULE_SHAPE', message, path: `${where}[${index}]` });
    }
  }

  // One graph over every read of the spec (F3 of docs/plans/field-relations.md):
  // no field may wait on itself, however far round, and no total may add up
  // an amount that waits on another total. Keys stand in for ids, as apply
  // rewrites them.
  for (const found of specGraphProblems(spec)) push(found);

  // A directory an answer can drop, and a field that points at it, must rise
  // and fall together. Otherwise the field would carry a foreign key to a
  // record that was never inserted, and the insert would fail at signup.
  const soundForPairing = records.filter(isObject);
  for (const directory of soundForPairing) {
    const gate = directory.whenAnswer;
    if (gate === undefined) continue;
    const wanted = [...gate].sort().join(',');
    for (const owner of soundForPairing) {
      for (const field of (Array.isArray(owner.fields) ? owner.fields : []).filter(isObject)) {
        if (field.targetRecordKey !== directory.key) continue;
        const held = [...(field.whenAnswer ?? [])].sort().join(',');
        if (held !== wanted) {
          push({
            code: 'WHEN_ANSWER_PAIR_BROKEN',
            message: `Directory "${directory.key}" ships only for [${wanted}], but field "${field.key}" of "${owner.key}" points at it under [${held || 'always'}]. Give both the same condition.`,
            path: `${at}.records[${owner.key}].fields[${field.key}]`,
          });
        }
      }
    }
  }

  // A seed row that points at another list's seed row needs that row first,
  // so two lists that point into each other cannot both be seeded.
  if (seedOrder(records.filter(isObject)) === null) {
    push({
      code: 'SEED_REFERENCE_RING',
      message: 'Two lists name each other\'s rows in their seed rows, so neither can be seeded first. Leave one side out.',
      path: at,
    });
  }

  // currencyRequired is a promise about the whole template. False must mean
  // no money anywhere; that is the entire point of Stock Register, T-10.6.
  const soundRecords = records.filter(isObject);
  // A line's money is the template's money too: an invoice line's amount.
  const moneyFields = recordsOf(spec).flatMap((r) =>
    (Array.isArray(r.fields) ? r.fields : []).filter((f) => isObject(f) && f.type === 'money'));
  if (spec.currencyRequired === false && moneyFields.length > 0) {
    push({
      code: 'CURRENCY_NOT_REQUIRED_BUT_MONEY',
      message: 'currencyRequired is false, yet a money field exists. One of the two is lying.',
      path: at,
    });
  }
  if (spec.currencyRequired === true && moneyFields.length === 0) {
    push({
      code: 'CURRENCY_REQUIRED_BUT_NO_MONEY',
      message: 'currencyRequired is true, yet no money field exists. The user would be asked a currency for nothing.',
      path: at,
    });
  }

  // Sample entries speak the main record's field keys. The main record is the
  // first of kind `record`, which every template must have.
  const main = soundRecords.find((r) => r.kind === 'record');
  if (!main) {
    // A list billed on a schedule is written into: Build makes its Charges record (L23), so lists and a plan are a
    // whole setup (L65: a gym that only bills its members gets no money form to make up a record).
    if (!soundRecords.some((r) => r.kind === 'directory' && r.chargePlan !== undefined)) push({ code: 'MAIN_RECORD_MISSING', message: 'A template with only directories records nothing.', path: at });
    return problems;
  }
  const mainKeys = new Set(
    (Array.isArray(main.fields) ? main.fields : []).filter(isObject).map((f) => f.key),
  );
  const samples = Array.isArray(spec.sampleEntries) ? spec.sampleEntries : [];
  if (samples.length < 1 || samples.length > 8) {
    push({ code: 'SAMPLE_COUNT', message: `A template ships 1 to 8 sample entries, not ${samples.length}.`, path: at });
  }
  for (const [index, sample] of samples.entries()) {
    const path = `${at}.sampleEntries[${index}]`;
    for (const [key, value] of Object.entries(sample ?? {})) {
      if (!mainKeys.has(key)) {
        push({ code: 'SAMPLE_FIELD_UNKNOWN', message: `Sample entry names field "${key}", which "${main.key}" does not declare.`, path });
      }
      if (typeof value === 'string') {
        push(resolveKey(catalogue, value, path, `sample value for "${key}"`));
      }
    }
  }

  return problems;
}
